/**
 * Authentication Context for Stage Door Access
 * Manages authentication state, tokens, and user session
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { handleUnauthorized } from '../api/auth';
import { getAttribution } from '../lib/attribution';

// API base URL - configured per environment
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

// Unauthenticated auth endpoints where a 401 is an expected outcome
// (e.g. a bad-password login) and must NOT trigger a force-logout redirect.
const UNAUTHENTICATED_AUTH_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/refresh',
] as const;

// User role types matching backend
export type UserRole = 'studio' | 'faculty' | 'admin' | 'super_admin';

// Authenticated user data
export interface AuthUser {
  id: string;
  email: string;
  role: UserRole;
  email_verified: boolean;
  studio_id?: string;
  faculty_id?: string;
}

// Authentication tokens
interface AuthTokens {
  access_token: string;
  refresh_token: string;
  expires_at: number;
}

// Auth context state
interface AuthState {
  user: AuthUser | null;
  tokens: AuthTokens | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

// Auth context value (state + methods)
interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<AuthUser>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<{ user_id: string; studio_id: string }>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string, passwordConfirm: string) => Promise<void>;
  refreshAuth: () => Promise<void>;
  getAccessToken: () => string | null;
}

// Registration data
export interface RegisterData {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  phone?: string;
  studio_name: string;
  address_line_1: string;
  address_line_2?: string;
  city: string;
  state: string;
  zip: string;
  /** Google Places locality place_id — set only when the city was picked from the autocomplete. */
  google_place_id?: string;
  /** Optional SMS/text-message consent opt-in (A2P/10DLC). */
  sms_consent?: boolean;
}

// API error response
interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: unknown;
  };
}

// Storage keys
const STORAGE_KEYS = {
  TOKENS: 'sda_auth_tokens',
  USER: 'sda_auth_user',
} as const;

// Create context
const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Custom hook to use auth context
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

/**
 * Parse API error response
 */
function parseApiError(error: unknown): string {
  if (error && typeof error === 'object' && 'error' in error) {
    const apiError = error as ApiError;
    return apiError.error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}

/**
 * Make authenticated API request
 */
async function apiRequest<T>(
  endpoint: string,
  options: RequestInit = {},
  accessToken?: string | null
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Merge any existing headers from options
  if (options.headers) {
    const existingHeaders = options.headers as Record<string, string>;
    Object.assign(headers, existingHeaders);
  }

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Force-logout on a rejected token for authenticated calls, but never for the
  // unauthenticated auth endpoints (a bad-password 401 must not redirect).
  if (!UNAUTHENTICATED_AUTH_ENDPOINTS.some((e) => endpoint.startsWith(e))) {
    handleUnauthorized(response);
  }

  const data = await response.json();

  if (!response.ok || data.success === false) {
    throw data;
  }

  return data.data as T;
}

/**
 * Load stored auth data from localStorage
 */
function loadStoredAuth(): { user: AuthUser | null; tokens: AuthTokens | null } {
  try {
    const tokensJson = localStorage.getItem(STORAGE_KEYS.TOKENS);
    const userJson = localStorage.getItem(STORAGE_KEYS.USER);

    const tokens = tokensJson ? (JSON.parse(tokensJson) as AuthTokens) : null;
    const user = userJson ? (JSON.parse(userJson) as AuthUser) : null;

    // Check if tokens are expired
    if (tokens && tokens.expires_at * 1000 < Date.now()) {
      // Tokens expired, clear stored data
      localStorage.removeItem(STORAGE_KEYS.TOKENS);
      localStorage.removeItem(STORAGE_KEYS.USER);
      return { user: null, tokens: null };
    }

    return { user, tokens };
  } catch {
    return { user: null, tokens: null };
  }
}

/**
 * Save auth data to localStorage
 */
function saveAuth(user: AuthUser, tokens: AuthTokens): void {
  localStorage.setItem(STORAGE_KEYS.TOKENS, JSON.stringify(tokens));
  localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user));
}

/**
 * Clear stored auth data
 */
function clearAuth(): void {
  localStorage.removeItem(STORAGE_KEYS.TOKENS);
  localStorage.removeItem(STORAGE_KEYS.USER);
}

/**
 * Auth Provider component
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    tokens: null,
    isLoading: true,
    isAuthenticated: false,
  });

  // Initialize auth state from storage
  useEffect(() => {
    const { user, tokens } = loadStoredAuth();
    setState({
      user,
      tokens,
      isLoading: false,
      isAuthenticated: !!user && !!tokens,
    });
  }, []);

  // Set up token refresh interval
  useEffect(() => {
    if (!state.tokens) return;

    // Refresh token 5 minutes before expiry
    const expiresAt = state.tokens.expires_at * 1000;
    const refreshAt = expiresAt - 5 * 60 * 1000;
    const now = Date.now();

    if (refreshAt <= now) {
      // Token is about to expire or expired, refresh now
      refreshAuth();
      return;
    }

    const timeoutId = setTimeout(() => {
      refreshAuth();
    }, refreshAt - now);

    return () => clearTimeout(timeoutId);
  }, [state.tokens?.expires_at]);

  /**
   * Login with email and password
   * @returns The authenticated user for role-based redirect
   */
  const login = useCallback(async (email: string, password: string): Promise<AuthUser> => {
    try {
      const response = await apiRequest<{
        access_token: string;
        refresh_token: string;
        expires_in: number;
        expires_at: number;
        user: AuthUser;
      }>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });

      const tokens: AuthTokens = {
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        expires_at: response.expires_at,
      };

      saveAuth(response.user, tokens);

      setState({
        user: response.user,
        tokens,
        isLoading: false,
        isAuthenticated: true,
      });

      return response.user;
    } catch (error) {
      throw new Error(parseApiError(error));
    }
  }, []);

  /**
   * Logout current user
   */
  const logout = useCallback(async (): Promise<void> => {
    const accessToken = state.tokens?.access_token;

    // Clear state immediately for responsive UI
    setState({
      user: null,
      tokens: null,
      isLoading: false,
      isAuthenticated: false,
    });
    clearAuth();

    // Notify backend (don't wait for response)
    if (accessToken) {
      try {
        await apiRequest('/api/auth/logout', {
          method: 'POST',
        }, accessToken);
      } catch {
        // Ignore logout errors - we've already cleared local state
      }
    }
  }, [state.tokens?.access_token]);

  /**
   * Register new studio owner account
   */
  const register = useCallback(async (data: RegisterData): Promise<{ user_id: string; studio_id: string }> => {
    setState(prev => ({ ...prev, isLoading: true }));

    try {
      const response = await apiRequest<{
        user_id: string;
        studio_id: string;
        message: string;
      }>('/api/auth/register', {
        method: 'POST',
        // SDA-267: attach stored campaign attribution out-of-band; the server
        // validates and stamps it (absent/invalid never blocks registration).
        body: JSON.stringify({ ...data, attribution: getAttribution() ?? undefined }),
      });

      setState(prev => ({ ...prev, isLoading: false }));

      return {
        user_id: response.user_id,
        studio_id: response.studio_id,
      };
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw new Error(parseApiError(error));
    }
  }, []);

  /**
   * Request password reset email
   */
  const forgotPassword = useCallback(async (email: string): Promise<void> => {
    await apiRequest('/api/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }, []);

  /**
   * Reset password with token
   */
  const resetPassword = useCallback(async (token: string, password: string, passwordConfirm: string): Promise<void> => {
    await apiRequest('/api/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password, password_confirm: passwordConfirm }),
    });
  }, []);

  /**
   * Refresh authentication tokens
   */
  const refreshAuth = useCallback(async (): Promise<void> => {
    const refreshToken = state.tokens?.refresh_token;
    if (!refreshToken) {
      setState({
        user: null,
        tokens: null,
        isLoading: false,
        isAuthenticated: false,
      });
      clearAuth();
      return;
    }

    try {
      const response = await apiRequest<{
        access_token: string;
        refresh_token: string;
        expires_in: number;
        expires_at: number;
      }>('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      const newTokens: AuthTokens = {
        access_token: response.access_token,
        refresh_token: response.refresh_token,
        expires_at: response.expires_at,
      };

      if (state.user) {
        saveAuth(state.user, newTokens);
      }

      setState(prev => ({
        ...prev,
        tokens: newTokens,
      }));
    } catch {
      // Refresh failed, clear auth state
      setState({
        user: null,
        tokens: null,
        isLoading: false,
        isAuthenticated: false,
      });
      clearAuth();
    }
  }, [state.tokens?.refresh_token, state.user]);

  /**
   * Get current access token
   */
  const getAccessToken = useCallback((): string | null => {
    return state.tokens?.access_token || null;
  }, [state.tokens?.access_token]);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    register,
    forgotPassword,
    resetPassword,
    refreshAuth,
    getAccessToken,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthContext;
